<template>
  <div class="dh-page-header">
    <div class="header-content">
      <div class="header-left">
        <slot name="left">
          <div v-if="showIcon" class="header-icon">
            <slot name="icon">
              <ElIcon v-if="iconComponent">
                <component :is="iconComponent" />
              </ElIcon>
            </slot>
          </div>
          <div class="header-text">
            <h1 class="header-title">
              <slot name="title">{{ title }}</slot>
            </h1>
            <p v-if="description" class="header-description">
              <slot name="description">{{ description }}</slot>
            </p>
          </div>
        </slot>
      </div>
      <div class="header-right">
        <slot name="right">
          <ElButton
            v-if="primaryButton && primaryButton.text"
            v-auth="primaryButton?.auth"
            :type="primaryButton.type || 'primary'"
            :size="primaryButton.size || 'large'"
            :disabled="primaryButton.disabled"
            :loading="primaryButton.loading"
            @click="handlePrimaryClick"
          >
            <ElIcon v-if="primaryButton.icon">
              <component :is="getIconComponent(primaryButton.icon)" />
            </ElIcon>
            {{ primaryButton.text }}
          </ElButton>
          <template v-if="secondaryButtons && secondaryButtons.length">
            <ElButton
              v-for="btn in secondaryButtons"
              :key="btn.key || btn.text"
              v-auth="btn.auth"
              :type="btn.type || 'default'"
              :size="btn.size || 'default'"
              :disabled="btn.disabled"
              :loading="btn.loading"
              @click="handleSecondaryClick(btn.key || btn.text)"
            >
              <ElIcon v-if="btn.icon">
                <component :is="getIconComponent(btn.icon)" />
              </ElIcon>
              {{ btn.text }}
            </ElButton>
          </template>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getIconComponent } from '@/utils/icon-map'

interface ButtonSize {
  size?: 'small' | 'default' | 'large'
}

interface ActionButton extends ButtonSize {
  text: string
  icon?: string
  type?: 'primary' | 'success' | 'warning' | 'danger' | 'default'
  disabled?: boolean
  loading?: boolean
  key?: string
  /** 权限指令值 */
  auth?: string
}

interface Props {
  /** 页面标题 */
  title?: string
  /** 页面副标题/描述 */
  description?: string
  /** 左侧图标名称（Element Plus 图标名称） */
  iconName?: string
  /** 主按钮配置 */
  primaryButton?: ActionButton
  /** 次按钮配置列表 */
  secondaryButtons?: ActionButton[]
}

/**
 * 页面头部通用组件
 */
const { title, description, iconName, primaryButton, secondaryButtons } = defineProps<Props>()

const emit = defineEmits<{
  (e: 'primary-click'): void
  (e: 'secondary-click', key: string): void
}>()
const iconComponent = computed(() => iconName ? getIconComponent(iconName) : null)
const showIcon = computed(() => !!iconName)

/**
 * 主按钮点击事件处理
 */
const handlePrimaryClick = (): void => {
  emit('primary-click')
}

/**
 * 次按钮点击事件处理
 * @param key 次按钮标识
 */
const handleSecondaryClick = (key: string): void => {
  emit('secondary-click', key)
}
</script>

<style scoped>
.dh-page-header {
  background: linear-gradient(135deg, var(--el-color-primary) 0%, var(--el-color-primary-light-3) 100%);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(var(--el-color-primary-rgb), 0.3);
  position: relative;
  overflow: hidden;
}

.dh-page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="%23ffffff" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  pointer-events: none;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.header-icon .el-icon {
  font-size: 32px;
  color: white;
}

.header-text {
  color: white;
}

.header-title {
  margin: 0 0 8px 0;
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(45deg, #ffffff, #e0e7ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-description {
  margin: 0;
  font-size: 16px;
  opacity: 0.9;
  font-weight: 400;
}

.header-right {
  display: flex;
  align-items: center;
}

.header-right :deep(.el-button) {
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.header-right :deep(.el-button:hover) {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}
</style>
